import { createTransport } from 'nodemailer'

const APP_NAME = 'Ask QUIEN'
const BRAND_COLOR = '#66A573'
const BRAND_COLOR_DARK = '#4d8a5a'
const APP_NAME_HTML = `<span style="font-family:'SFMono-Regular',Consolas,'Liberation Mono',monospace;font-weight:700;color:${BRAND_COLOR};">Ask QUIEN</span>`
const LOGO_URL = 'https://quality-intelligence.co/brand/qi-mark.png'

function getBaseUrl(request: Request) {
  const envUrl = process.env.NEXTAUTH_URL ?? process.env.AUTH_URL
  if (envUrl) return envUrl.replace(/\/$/, '')

  const url = new URL(request.url)
  return `${url.protocol}//${url.host}`
}

export function buildAdminInviteUrl({ email, request }: { email: string; request: Request }) {
  const baseUrl = getBaseUrl(request)
  const normalizedEmail = email.trim().toLowerCase()
  return `${baseUrl}/login?${new URLSearchParams({ email: normalizedEmail })}`
}

export function buildAdminInviteEmail({
  inviteUrl,
  host,
}: {
  inviteUrl: string
  host: string
}): { subject: string; html: string; text: string } {
  const subject = `You have been invited to ${APP_NAME}`

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${subject}</title>
</head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:480px;">
          <tr>
            <td align="center" style="padding-bottom:24px;">
              <img src="${LOGO_URL}" width="64" height="64" alt="${APP_NAME}" style="display:block;border-radius:12px;" />
              <p style="margin:12px 0 0;font-size:18px;">${APP_NAME_HTML}</p>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;border-radius:12px;border:1px solid #e4e4e7;padding:40px 40px 32px;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding-bottom:8px;">
                    <p style="margin:0;font-size:22px;font-weight:700;color:#18181b;letter-spacing:-0.4px;">You have been invited</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom:28px;">
                    <p style="margin:0;font-size:15px;line-height:1.6;color:#52525b;">
                      An administrator has added you to ${APP_NAME_HTML}. Click below to open the sign-in page, then request your one-time magic link.
                    </p>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-bottom:28px;">
                    <a href="${inviteUrl}"
                       style="display:inline-block;background-color:${BRAND_COLOR};color:#ffffff;text-decoration:none;font-size:15px;font-weight:600;padding:14px 32px;border-radius:8px;letter-spacing:0.1px;"
                       onmouseover="this.style.backgroundColor='${BRAND_COLOR_DARK}'"
                       onmouseout="this.style.backgroundColor='${BRAND_COLOR}'">
                      Sign in to ${APP_NAME}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td style="padding-bottom:24px;border-top:1px solid #f4f4f5;padding-top:20px;">
                    <p style="margin:0 0 6px;font-size:12px;color:#71717a;font-weight:500;text-transform:uppercase;letter-spacing:0.5px;">Button not working?</p>
                    <p style="margin:0;font-size:12px;color:#71717a;">Paste this link into your browser:</p>
                    <p style="margin:6px 0 0;font-size:11px;word-break:break-all;">
                      <a href="${inviteUrl}" style="color:${BRAND_COLOR};text-decoration:none;">${inviteUrl}</a>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p style="margin:0;font-size:12px;color:#a1a1aa;line-height:1.5;">
                      If you were not expecting this invitation, you can safely ignore this email.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding-top:24px;">
              <p style="margin:0;font-size:12px;color:#a1a1aa;">&copy; ${new Date().getFullYear()} Quality Intelligence &middot; <a href="https://${host}" style="color:#a1a1aa;text-decoration:none;">${host}</a></p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`

  const text = `You have been invited to ${APP_NAME}\n\nAn administrator has added you to ${APP_NAME}. Open the sign-in page below, then request your one-time magic link.\n\n${inviteUrl}\n\nIf you were not expecting this invitation, you can safely ignore this email.\n\n- ${APP_NAME} / ${host}`

  return { subject, html, text }
}

export async function sendAdminInviteEmail({
  email,
  request,
}: {
  email: string
  request: Request
}) {
  const normalizedEmail = email.trim().toLowerCase()
  const inviteUrl = buildAdminInviteUrl({ email: normalizedEmail, request })

  if (!process.env.EMAIL_SERVER_HOST) {
    if (process.env.NODE_ENV !== 'test') {
      console.log(`\n[admin-invite][dev] Invitation link for ${normalizedEmail}:\n${inviteUrl}\n`)
    }
    return
  }

  const transport = createTransport({
    host: process.env.EMAIL_SERVER_HOST,
    port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
    auth: process.env.EMAIL_SERVER_USER
      ? {
          user: process.env.EMAIL_SERVER_USER,
          pass: process.env.EMAIL_SERVER_PASSWORD,
        }
      : undefined,
  })
  const { host } = new URL(inviteUrl)
  const { subject, html, text } = buildAdminInviteEmail({ inviteUrl, host })

  const result = await transport.sendMail({
    to: normalizedEmail,
    from: process.env.EMAIL_FROM,
    subject,
    html,
    text,
  })
  const failed = [...(result.rejected ?? []), ...(result.pending ?? [])].filter(Boolean)
  if (failed.length) throw new Error(`Invite email (${failed.join(', ')}) could not be sent`)
}
